import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntityListLayout, FilterPanel, SupplierFilters, Pagination } from '../../components';
import type { SupplierFilterValues } from '../../components/FilterPanel/SupplierFilters';
import { Card } from '../../components/ui/Card';
import { ActionMenu } from '../../components/ui/ActionMenu';
import { Table, TableHeader, TableBody, TableRow, TableHeaderCell, TableCell } from '../../components/ui/Table';
import { LoadingOverlay } from '../../components/ExportOverlay';
import { useDeleteSupplier, useSuppliers } from '../../hooks/useSuppliers';
import { Search, ChevronsUpDown, ChevronUp, ChevronDown, Pencil, Eye, Trash2 } from 'lucide-react';

interface Supplier {
  id: number;
  name: string;
  country?: string;
  vineyards: number;
  bulkWine: number;
  cleanskins: number;
  fruits: number;
  users: number;
  dateCreated: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
};

export const SuppliersPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Column Visibility Controls - Change true/false to show/hide columns
  const showSupplier = true;
  const showCountry = true;
  const showVineyards = false;//true;
  const showBulkWine = true;
  const showCleanskins = false;//true;
  const showFruits = false;//true;
  const showUsers = true;
  const showDateCreated = true;
  const showActions = true;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(10);
  const [sortField, setSortField] = useState<keyof Supplier>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<SupplierFilterValues | null>(null);
  const [currentFilters, setCurrentFilters] = useState<SupplierFilterValues | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const queryParams = useMemo(() => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: itemsPerPage === 'all' ? '1000000' : itemsPerPage.toString(),
    });
    // Search term takes priority over filter panel name filter
    if (searchTerm) {
      params.append('name', searchTerm);
    } else if (activeFilters) {
      if (activeFilters.supplierName) params.append('name', activeFilters.supplierName);
      if (activeFilters.countryId) params.append('countryId', activeFilters.countryId);
      if (activeFilters.regionId) params.append('regionId', activeFilters.regionId);
    }
    return params;
  }, [activeFilters, currentPage, itemsPerPage, searchTerm]);

  const { data: suppliersResponse, isLoading: loading, isFetching } = useSuppliers(queryParams);
  const deleteSupplier = useDeleteSupplier();
  const suppliers = (suppliersResponse?.data || []).map((s: any) => ({
    id: parseInt(s.id),
    name: s.name,
    country: s.countryName,
    vineyards: parseInt(s.vineyards),
    bulkWine: parseInt(s.bulkWine),
    cleanskins: parseInt(s.cleanskins),
    fruits: parseInt(s.fruits),
    users: parseInt(s.users),
    dateCreated: s.dateCreated,
  }));
  const pagination = suppliersResponse?.pagination || {
    page: currentPage,
    limit: itemsPerPage,
    total: 0,
    totalPages: 0,
  };

  const handleSort = (field: keyof Supplier) => {
    if (field === 'id') return; // Don't sort by id
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon: React.FC<{ field: keyof Supplier }> = ({ field }) => {
    if (field === 'id') return null;
    if (sortField !== field) {
      return <ChevronsUpDown size={14} color="#D0D5DD" className="inline-block ml-1" aria-hidden="true" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp size={14} color="#404040" className="inline-block ml-1" aria-hidden="true" />
    ) : (
      <ChevronDown size={14} color="#404040" className="inline-block ml-1" aria-hidden="true" />
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const sortedSuppliers = [...suppliers].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const modifier = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * modifier;
      }
      return ((aVal as number) - (bVal as number)) * modifier;
    });

  const handleDeleteSupplier = async (supplier: Supplier) => {
    if (!window.confirm(`Delete supplier "${supplier.name}"?`)) {
      return;
    }

    try {
      await deleteSupplier.mutateAsync(supplier.id);
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to delete supplier. Please try again.'));
    }
  };

  const handlePageSizeChange = (pageSize: number | 'all') => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  return (
    <EntityListLayout
      title="Suppliers"
      addButtonLabel="Add New Supplier"
      onAdd={() => navigate('/suppliers/new')}
      entriesCount={pagination.total}
      onOpenFilter={() => setIsFilterOpen(true)}
      currentPageSize={itemsPerPage}
      onPageSizeChange={handlePageSizeChange}
      toolbarActions={
        <div className="relative">
          <input
            type="search"
            placeholder="Search suppliers..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:border-transparent"
            style={{
              width: '300px',
              height: '38px',
              fontFamily: 'Oxygen',
              fontSize: '14px',
              borderColor: '#D0D5DD',
              backgroundColor: '#F6F5F3',
            }}
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            size={20}
            color="#667085"
            strokeWidth={1.67}
            aria-hidden="true"
          />
        </div>
      }
    >
        <div className="relative">
          <LoadingOverlay visible={isFetching && suppliers.length > 0} message="Loading..." />
          <Card noPadding>
          <Table>
            <TableHeader>
              <TableRow>
                {showSupplier && (
                  <TableHeaderCell onClick={() => handleSort('name')} style={{ cursor: 'pointer', width: '25%' }}>
                    Supplier
                    <SortIcon field="name" />
                  </TableHeaderCell>
                )}
                {showCountry && (
                  <TableHeaderCell onClick={() => handleSort('country')} style={{ cursor: 'pointer', width: '15%' }}>
                    Country
                    <SortIcon field="country" />
                  </TableHeaderCell>
                )}
                {showVineyards && (
                  <TableHeaderCell onClick={() => handleSort('vineyards')} style={{ cursor: 'pointer', width: '10%' }}>
                    Vineyards
                    <SortIcon field="vineyards" />
                  </TableHeaderCell>
                )}
                {showBulkWine && (
                  <TableHeaderCell onClick={() => handleSort('bulkWine')} style={{ cursor: 'pointer', width: '10%' }}>
                    Bulk Wine
                    <SortIcon field="bulkWine" />
                  </TableHeaderCell>
                )}
                {showCleanskins && (
                  <TableHeaderCell onClick={() => handleSort('cleanskins')} style={{ cursor: 'pointer', width: '10%' }}>
                    CleanSkins
                    <SortIcon field="cleanskins" />
                  </TableHeaderCell>
                )}
                {showFruits && (
                  <TableHeaderCell onClick={() => handleSort('fruits')} style={{ cursor: 'pointer', width: '10%' }}>
                    Fruits
                    <SortIcon field="fruits" />
                  </TableHeaderCell>
                )}
                {showUsers && (
                  <TableHeaderCell onClick={() => handleSort('users')} style={{ cursor: 'pointer', width: '8%' }}>
                    Users
                    <SortIcon field="users" />
                  </TableHeaderCell>
                )}
                {showDateCreated && (
                  <TableHeaderCell onClick={() => handleSort('dateCreated')} style={{ cursor: 'pointer', width: '15%' }}>
                    Date Created
                    <SortIcon field="dateCreated" />
                  </TableHeaderCell>
                )}
                {showActions && (
                  <TableHeaderCell style={{ width: '12%', textAlign: 'center' }}>Actions</TableHeaderCell>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSuppliers.map((supplier) => (
                <TableRow 
                  key={supplier.id}
                  onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {showSupplier && <TableCell className="font-medium">{supplier.name}</TableCell>}
                  {showCountry && <TableCell>{supplier.country}</TableCell>}
                  {showVineyards && <TableCell>{supplier.vineyards}</TableCell>}
                  {showBulkWine && <TableCell>{supplier.bulkWine}</TableCell>}
                  {showCleanskins && <TableCell>{supplier.cleanskins}</TableCell>}
                  {showFruits && <TableCell>{supplier.fruits}</TableCell>}
                  {showUsers && <TableCell>{supplier.users}</TableCell>}
                  {showDateCreated && <TableCell>{formatDate(supplier.dateCreated)}</TableCell>}
                  {showActions && (
                    <TableCell>
                      <div className="flex items-center justify-center">
                        <ActionMenu
                          ariaLabel={`Actions for ${supplier.name}`}
                          items={[
                            {
                              label: 'View',
                              icon: <Eye size={16} />,
                              onClick: () => navigate(`/suppliers/${supplier.id}`),
                            },
                            {
                              label: 'Edit',
                              icon: <Pencil size={16} />,
                              onClick: () => navigate(`/suppliers/${supplier.id}/edit`),
                            },
                            {
                              label: 'Delete',
                              icon: <Trash2 size={16} />,
                              destructive: true,
                              onClick: () => void handleDeleteSupplier(supplier),
                            },
                          ]}
                        />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Card>
        </div>

        {/* Pagination Controls */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalEntries={pagination.total}
          pageSize={typeof itemsPerPage === 'number' ? itemsPerPage : pagination.total}
          onPageChange={setCurrentPage}
          loading={loading}
        />

        <FilterPanel 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)}
          onFilter={() => {
            // Close panel first (slide out animation)
            setIsFilterOpen(false);
            // Wait for animation to complete, then apply filters
            setTimeout(() => {
              if (currentFilters) {
                setActiveFilters(currentFilters);
              }
              setCurrentPage(1);
            }, 300);
          }}
          onClear={() => {
            // Close panel first (slide out animation)
            setIsFilterOpen(false);
            // Wait for animation to complete, then clear filters
            setTimeout(() => {
              setActiveFilters(null);
              setCurrentFilters(null);
              setCurrentPage(1);
            }, 300);
          }}
        >
          <SupplierFilters 
            initialValues={activeFilters || undefined}
            onFiltersReady={(getFilters) => {
              setCurrentFilters(getFilters());
            }}
          />
        </FilterPanel>
      </EntityListLayout>
  );
};

export default SuppliersPage;
